import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Search, Download, X, MapPin, Calendar, ImageIcon, ArrowLeft, Package } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ShareButton from "../components/SocialShare";
import api, { fileUrl, brandedUrl, downloadUrl, zipDownloadUrl, shareGalleryUrl, sharePhotoUrl } from "../lib/api";
import { toast } from "sonner";

export default function EventDetail() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialBib = searchParams.get("bib") || "";

  const [event, setEvent] = useState(null);
  const [bib, setBib] = useState(initialBib);
  const [photos, setPhotos] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/public/events/${slug}`)
      .then((res) => setEvent(res.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  // Track QR scan on landing (fire-once per session)
  useEffect(() => {
    if (searchParams.get("src") !== "qr") return;
    const key = `rs_qr_scan_${slug}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    api.post(`/public/events/${slug}/track`, { kind: "qr_scan" }).catch(() => {});
  }, [slug, searchParams]);

  const performSearch = useCallback(async (b) => {
    if (!b) return;
    setLoading(true);
    setSearched(true);
    try {
      const res = await api.get(`/public/events/${slug}/search`, { params: { bib: b } });
      setPhotos(res.data.photos);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Search failed");
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    if (initialBib) performSearch(initialBib);
  }, [initialBib, performSearch]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!bib.trim()) return;
    setSearchParams({ bib });
    performSearch(bib.trim());
  };

  const galleryCaption = event
    ? `Check out my photos from ${event.name} on RaceSnap!`
    : "Check out my race photos on RaceSnap!";
  const photoCaption = event
    ? `A moment from ${event.name} — captured on RaceSnap.`
    : "A race moment captured on RaceSnap.";

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center" data-testid="event-not-found">
            <p className="kicker mb-3">404</p>
            <h1 className="font-display text-3xl font-bold text-[#0F172A]">Event not found</h1>
            <p className="mt-2 text-sm text-[#475569]">This event is either unpublished or doesn&apos;t exist.</p>
            <Link to="/" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#5B4CFF] hover:underline">
              <ArrowLeft size={14} /> Back home
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white pb-16 md:pb-0">
      <Navbar />

      {/* Mobile sticky bib search — appears once event is loaded */}
      {event && (
        <div className="md:hidden sticky top-16 z-30 bg-white border-b border-[#E2E8F0]">
          <form onSubmit={handleSubmit} className="px-4 py-3 flex gap-2" data-testid="mobile-sticky-search">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] font-mono tracking-widest text-[#5B4CFF] pointer-events-none">BIB #</span>
              <input
                type="text"
                inputMode="numeric"
                value={bib}
                onChange={(e) => setBib(e.target.value)}
                placeholder="0000"
                className="w-full bg-white border border-[#E2E8F0] pl-[58px] pr-3 py-2.5 font-mono font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#5B4CFF]"
                data-testid="mobile-bib-input"
              />
            </div>
            <button type="submit" className="bg-[#5B4CFF] hover:bg-[#4a3dde] text-white font-semibold px-4 min-h-[44px] transition-colors inline-flex items-center gap-1.5" data-testid="mobile-search-btn">
              <Search size={16} />
            </button>
          </form>
        </div>
      )}

      <section className="border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <Link to="/" className="text-xs text-[#475569] hover:text-[#0F172A] inline-flex items-center gap-1 mb-6">
            <ArrowLeft size={12} /> ALL EVENTS
          </Link>

          {event && (
            <>
              <p className="kicker mb-3" data-testid="event-location">{event.city?.toUpperCase()} · {event.country?.toUpperCase()}</p>
              <h1 className="font-display text-3xl md:text-5xl lg:text-6xl font-extrabold text-[#0F172A] tracking-tighter" data-testid="event-title">
                {event.name}
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#475569]">
                <span className="inline-flex items-center gap-2"><Calendar size={14} />{event.event_date}</span>
                <span className="inline-flex items-center gap-2"><MapPin size={14} />{event.city}, {event.country}</span>
                <span className="inline-flex items-center gap-2"><ImageIcon size={14} />{event.photo_count} photos</span>
              </div>
              {event.description && (
                <p className="mt-6 max-w-2xl text-[#475569] leading-relaxed">{event.description}</p>
              )}

              <form onSubmit={handleSubmit} className="mt-8 md:mt-10 bg-white border border-[#E2E8F0] p-2 flex flex-col sm:flex-row gap-2 max-w-2xl" data-testid="event-search-form">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] font-mono tracking-widest text-[#5B4CFF] pointer-events-none">BIB #</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="Enter your race number"
                    value={bib}
                    onChange={(e) => setBib(e.target.value)}
                    className="w-full bg-white border border-[#E2E8F0] pl-[58px] pr-3 py-3 text-base font-mono font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#5B4CFF]"
                    data-testid="event-bib-input"
                  />
                </div>
                <button type="submit" className="bg-[#5B4CFF] hover:bg-[#4a3dde] text-white font-semibold px-6 py-3 inline-flex items-center justify-center gap-2 transition-colors min-h-[48px]" data-testid="event-search-btn">
                  <Search size={16} /> Search
                </button>
              </form>
            </>
          )}
        </div>
      </section>

      <section className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          {loading && (
            <div className="text-center py-20" data-testid="loading-state">
              <p className="font-mono text-sm tracking-widest text-[#475569]">SEARCHING…</p>
            </div>
          )}

          {!loading && searched && photos.length === 0 && (
            <div className="border border-dashed border-[#E2E8F0] p-12 text-center" data-testid="no-photos">
              <p className="font-display text-xl text-[#0F172A] mb-2">No photos found for bib #{bib}</p>
              <p className="text-sm text-[#475569]">Photographers may still be uploading. Check back soon, or double-check your bib number.</p>
            </div>
          )}

          {!loading && photos.length > 0 && (
            <>
              <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                <div>
                  <p className="kicker mb-2">RESULTS FOR BIB</p>
                  <h2 className="font-display text-2xl md:text-3xl font-bold text-[#0F172A]" data-testid="results-count">
                    #{bib} · {photos.length} photo{photos.length === 1 ? "" : "s"}
                  </h2>
                </div>
                <div className="flex flex-wrap gap-3">
                  <ShareButton
                    shareUrl={shareGalleryUrl(slug, bib)}
                    defaultCaption={galleryCaption}
                    title={`Share your ${photos.length} photo${photos.length === 1 ? "" : "s"}`}
                    label="Share gallery"
                    variant="primary"
                    testId="share-gallery-btn"
                    trackContext={{ slug, share_type: "gallery", bib }}
                  />
                  <a
                    href={zipDownloadUrl(slug, bib)}
                    className="inline-flex items-center gap-2 bg-[#0F172A] hover:bg-black text-white font-semibold px-5 py-3 text-sm transition-colors"
                    data-testid="download-zip-btn"
                  >
                    <Package size={16} /> Download all (.zip)
                  </a>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {photos.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setPreview(p)}
                    className="group relative aspect-square bg-[#F8FAFC] border border-[#E2E8F0] hover:border-[#5B4CFF] overflow-hidden"
                    data-testid={`photo-${p.id}`}
                  >
                    <img src={fileUrl(p.id)} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {preview && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
          data-testid="photo-preview-modal"
        >
          <div className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setPreview(null)}
              className="absolute -top-12 right-0 text-white inline-flex items-center gap-2 text-sm"
              data-testid="preview-close-btn"
            >
              <X size={18} /> Close
            </button>
            <img src={brandedUrl(preview.id)} alt="" className="w-full max-h-[80vh] object-contain bg-black border border-white/10" />
            <div className="mt-4 flex flex-wrap gap-3 justify-end">
              <ShareButton
                shareUrl={sharePhotoUrl(preview.id)}
                defaultCaption={photoCaption}
                title="Share this photo"
                label="Share photo"
                variant="ghost"
                testId="preview-share-btn"
                trackContext={{ slug, share_type: "photo", photo_id: preview.id, bib }}
              />
              <a
                href={downloadUrl(preview.id)}
                className="inline-flex items-center gap-2 bg-[#5B4CFF] hover:bg-[#4a3dde] text-white font-semibold px-5 py-2.5 text-sm transition-colors"
                data-testid="preview-download-btn"
              >
                <Download size={14} /> Download original
              </a>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
